<template>
  <div class="table-container">
    <div class="table-heading">
      <div class="table-heading__title">
        Submissions overview
        <span class="table-heading__count">{{ total }}</span>
      </div>
      <div class="table-heading__actions">
        <select
          v-model="selectedStatus"
          class="table-filter__status"
          @change="$emit('status-change', selectedStatus)"
        >
          <option value="">{{ $t('submissions.filter_all') }}</option>
          <option value="received">{{ $t('submissions.filter_complete') }}</option>
          <option value="partial">{{ $t('submissions.filter_incomplete') }}</option>
        </select>
        <Btn
          class="hide-on-mobile"
          variant="tertiary"
          size="sm"
          icon="export"
          :text="isExporting ? $t('submissions.exporting') : $t('submissions.export_to_csv')"
          @click="$emit('export')"
          :loading="isExporting"
        ></Btn>
      </div>
    </div>
    <EasyDataTable
      :headers="headers"
      :items="formattedSubmissions"
      :loading="loading"
      :server-items-length="total"
      v-model:server-options="serverOptions"
      buttons-pagination
      @click-row="$emit('row-click', $event)"
      table-class-name="custom-table"
    >
      <template #item-lastUpdated="{ id, lastUpdated, status }">
        <TableRowLink
          :to="`/flow/${form.id}/submissions/${id}`"
          :aria-label="`Submission ${lastUpdated}`"
        >
          <span class="table-value">
            <BaseIcon v-if="status === 'partial'" name="draw" size="16" />
            <span v-else class="table-dot"></span>
            {{ lastUpdated }}
          </span>
        </TableRowLink>
      </template>
    </EasyDataTable>
  </div>
</template>

<script>
  import { ref, watch, computed, getCurrentInstance } from 'vue';
  import { formatDate } from '@/utils/date';
  import { ELEMENTS } from '@/constants/elements';
  import EasyDataTable from 'vue3-easy-data-table';
  import 'vue3-easy-data-table/dist/style.css';

  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';
  import TableRowLink from '@/components/tables/TableRowLink.vue';
  export default {
    components: {
      EasyDataTable,
      BaseIcon,
      Btn,
      TableRowLink,
    },

    props: {
      submissions: {
        type: Array,
        required: true,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      form: {
        type: Object,
        required: true,
      },
      isExporting: {
        type: Boolean,
        default: false,
      },
      total: {
        type: Number,
        required: true,
      },
      currentPage: {
        type: Number,
        required: true,
      },
      pageSize: {
        type: Number,
        required: true,
      },
      initialStatus: {
        type: String,
        default: 'received',
      },
    },

    emits: ['row-click', 'export', 'page-change', 'status-change'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const selectedStatus = ref(props.initialStatus);
      const serverOptions = ref({
        page: props.currentPage,
        rowsPerPage: props.pageSize,
      });

      // Watch for changes in initialStatus prop and sync selectedStatus
      watch(
        () => props.initialStatus,
        (newStatus) => {
          selectedStatus.value = newStatus;
        },
        { immediate: true }
      );

      // Watch for changes in serverOptions and emit page-change event
      watch(
        serverOptions,
        (newOptions) => {
          emit('page-change', {
            page: newOptions.page,
            limit: newOptions.rowsPerPage,
          });
        },
        { deep: true }
      );

      const headers = [
        { text: 'Last updated at', value: 'lastUpdated' },
        { text: 'Name', value: 'leadName' },
        { text: 'Email', value: 'leadEmail' },
      ];

      function getFirstTwoNonContentFields(form) {
        // Flatten all elements from all pages (pages can be missing on
        // summary form objects coming from the list endpoint)
        const allElements = (form?.pages || []).flatMap((page) => page.elements || []);
        // Find first two non-content fields
        return allElements
          .filter((element) => {
            // Use the ELEMENTS constant to check if it's not in content category
            const elementDefinition = ELEMENTS.find((e) => e.type === element.type);
            return elementDefinition && elementDefinition.category !== 'content';
          })
          .slice(0, 2);
      }

      function getAnswersForFields(submission, formFields, form) {
        const answers = Array.isArray(submission.data) ? submission.data : [];
        return formFields
          .map((field) => {
            const answer = answers.find((a) => a.fieldId === field.id);
            if (!answer?.value) return null;

            // For multiple choice, map the IDs to actual choice content
            let displayValue = answer.value;
            if (
              ['multiple-choice', 'picture-choice'].includes(field.type) &&
              Array.isArray(answer.value)
            ) {
              displayValue = answer.value
                .map((choiceId) => field.props.choices.find((c) => c.id === choiceId)?.content)
                .filter(Boolean)
                .join(', ');
            } else if (field.type === 'single-choice') {
              const choice = field.props.choices.find((c) => c.id === answer.value);
              displayValue = choice?.content || 'No value provided';
            } else if (field.type === 'kvk') {
              displayValue = `${answer.value.businessInfo?.name} (${answer.value.businessInfo?.kvkNumber})`;
            }

            return {
              fieldId: field.id,
              fieldName: field.props.label,
              value: displayValue,
            };
          })
          .filter(Boolean);
      }

      // Get the first two non-content fields from the form
      const firstTwoFormFields = getFirstTwoNonContentFields(props.form);

      // Add headers for these fields
      firstTwoFormFields.forEach((field) => {
        headers.push({
          text: field.props.label,
          value: `field_${field.id}`,
        });
      });

      const formattedSubmissions = computed(() => {
        return props.submissions.map((submission) => {
          const formattedDate = formatDate(submission.updated_at);

          // Get answers for our two fields
          const fieldAnswers = getAnswersForFields(submission, firstTwoFormFields, props.form);

          // Create the submission object with dynamic fields
          const submissionObj = {
            ...submission,
            lastUpdated: formattedDate,
            leadName: submission.lead?.name || '',
            leadEmail: submission.lead?.email || '',
          };

          // Add the field values to the submission object
          fieldAnswers.forEach((field) => {
            submissionObj[`field_${field.fieldId}`] = field.value;
          });
          return submissionObj;
        });
      });

      return {
        headers,
        formattedSubmissions,
        serverOptions,
        selectedStatus,
      };
    },
  };
</script>

<style lang="scss" scoped>
  // All table styling is now handled by the global _tables.scss
  // Only component-specific styles remain here

  .icon {
    color: var(--slate-500);
  }

  .hide-on-mobile {
    @include lg-down {
      display: none !important;
    }
  }
</style>
